import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { AUTH_STORAGE_KEYS, AuthState, AuthUser, Rolle } from '../types/auth';
import { authService } from '../services/authService';

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (updated: AuthUser) => void;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const stored = authService.getStoredAuth();
  const [user, setUser] = useState<AuthUser | null>(stored.user);
  const [token, setToken] = useState<string | null>(stored.token);

  useEffect(() => {
    // Keep state in sync if storage changes in another tab
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === 'gks_auth_token' || e.key === 'gks_auth_user') {
        const next = authService.getStoredAuth();
        setUser(next.user);
        setToken(next.token);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const state = await authService.login({ username, password });
    setUser(state.user);
    setToken(state.token);
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setToken(null);
  }, []);

  const updateUser = useCallback((updated: AuthUser) => {
    setUser(updated);
    localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(updated));
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    token,
    login,
    logout,
    updateUser,
    isAdmin: !!user && user.rolle === Rolle.ADMIN,
  }), [user, token, login, logout, updateUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
